import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { modelingApi } from '@/api';
import { QUERY_KEY_CONST } from '@/constant';
import type {
  FrameGraphData,
  Modeling,
  ModelingApi,
  ModelingSearchParams,
} from '@/type';

import { useTypedSearchParams } from '../searchParams';

const { MODELING_KEYS } = QUERY_KEY_CONST;

export const useModelingListQuery = (
  param?: Partial<ModelingApi.GetList.Request>,
) => {
  const { get } = useTypedSearchParams<ModelingSearchParams>();
  const limit = get('modelingLimit') ? Number(get('modelingLimit')) : 0;
  const offset = get('modelingOffset') ? Number(get('modelingOffset')) : 0;
  const keyword = get('modelingKeyword');

  const listParams: ModelingApi.GetList.Request = {
    ...param,
    limit,
    offset,
    keyword,
  };

  Object.entries(listParams).forEach(([key, value]) => {
    if (value == null) delete listParams[key as keyof typeof listParams];
  });

  return useQuery({
    queryKey: MODELING_KEYS.getList(listParams),
    queryFn: () =>
      modelingApi.getList(listParams).then(res => res.data.resultData),
  });
};

export const useModelingDetailQuery = (id: Modeling['id']) => {
  return useQuery({
    queryKey: MODELING_KEYS.getDetail(id),
    queryFn: () => modelingApi.get({ id }).then(res => res.data.resultData),
  });
};

export const useModelingListInfiniteQuery = (
  param?: Omit<Partial<ModelingApi.GetList.Request>, 'cursor'>,
) => {
  return useInfiniteQuery({
    queryKey: MODELING_KEYS.getList(param),
    queryFn: ({ pageParam: cursor }) =>
      modelingApi
        .getList({ ...param, cursor })
        .then(res => ({ list: res.data.resultData, cursor: res.data.cursor })),
    initialPageParam: '',
    getNextPageParam: nextPage => nextPage.cursor,
    getPreviousPageParam: prevPage => prevPage.cursor,
  });
};

export const useModelingListCountQuery = (
  param?: Partial<ModelingApi.GetCount.Request>,
) => {
  return useQuery({
    queryKey: MODELING_KEYS.getCount(param),
    queryFn: () => modelingApi.getCount(param).then(res => res.data.resultData),
  });
};

export const useModelingRemoveQuery = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ModelingApi.Remove.Request) =>
      modelingApi.remove(data).then(res => res.data.resultData),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: MODELING_KEYS.getList(),
      });
    },
  });
};

export const useModelingUpdateQuery = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ModelingApi.Update.Request) =>
      modelingApi.update(data).then(res => res.data.resultData),
    onSuccess() {
      queryClient.invalidateQueries({
        queryKey: MODELING_KEYS.getList(),
      });
    },
  });
};

export const useModelingRequestTrainingQuery = () => {
  return useMutation({
    mutationFn: (data: ModelingApi.RequestTraining.Request) =>
      modelingApi.requestTraining(data).then(res => res.data.resultData),
  });
};

export const useModelingDeployQuery = () => {
  return useMutation({
    mutationFn: (data: ModelingApi.Deploy.Request) =>
      modelingApi.deploy(data).then(res => res.data.resultData),
  });
};

export const useModelingRemoveDeployment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ModelingApi.Deploy.Request) =>
      modelingApi.removeDeployment(data).then(res => res.data.resultData),
    onSuccess() {
      queryClient.invalidateQueries({
        queryKey: MODELING_KEYS.getList(),
      });
    },
  });
};

export const useModelingPerformanceQuery = (id: Modeling['id']) => {
  // TODO: 하드코딩 제거
  const frameGraphDataOrdering = [
    'vehicle',
    'pedestrian',
    'cyclist',
    'distance',
  ] as (keyof FrameGraphData)[];

  const reorderGraphData = (data: FrameGraphData) =>
    frameGraphDataOrdering.reduce(
      (acc, k) => ({ ...acc, [k]: data[k] }),
      {} as FrameGraphData,
    );

  return useQuery({
    queryKey: MODELING_KEYS.getPerformance(id),
    queryFn: () =>
      modelingApi.getPerformance({ pModelingID: id }).then(res => {
        return {
          ...res.data.resultData,
          graphData: res.data.resultData.graphData.map(graphData =>
            reorderGraphData(graphData),
          ),
        };
      }),
  });
};
